// ─── Focusable Selector ───────────────────────────────────────────────────────

/**
 * CSS selector matching all natively focusable / interactive elements.
 * Excludes: disabled, aria-hidden, tabindex="-1" elements.
 *
 * Note: `[tabindex]:not([tabindex="-1"])` catches custom focusable elements
 * including those with tabindex="0".
 */
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable="true"]',
  'audio[controls]',
  'video[controls]',
  'details > summary',
].join(',');

// ─── Visibility ───────────────────────────────────────────────────────────────

/**
 * Returns false if the element is hidden via CSS display:none,
 * visibility:hidden, or has zero physical size.
 *
 * Note: Does NOT check `opacity` — opacity:0 elements can still receive focus
 * (e.g., visually-hidden form fields).
 */
export function isElementVisible(el: Element): boolean {
  if (!(el instanceof HTMLElement)) return false;

  // Fastest check first — offsetParent is null for display:none elements
  // (except for position:fixed elements, which we handle below)
  if (!el.offsetParent && el.offsetHeight === 0 && el.offsetWidth === 0) {
    // Could be position:fixed — verify via computed style
    const style = window.getComputedStyle(el);
    if (style.display === 'none') return false;
    if (style.position !== 'fixed') return false;
  }

  const style = window.getComputedStyle(el);
  if (style.visibility === 'hidden') return false;
  if (style.display === 'none') return false;

  return true;
}

/**
 * Returns true if the element (or any ancestor) has `aria-hidden="true"`.
 * aria-hidden elements are invisible to assistive technology and should not
 * participate in keyboard navigation.
 */
export function isAriaHidden(el: Element): boolean {
  let node: Element | null = el;
  while (node) {
    if (node.getAttribute('aria-hidden') === 'true') return true;
    node = node.parentElement;
  }
  return false;
}

// ─── Core Queries ─────────────────────────────────────────────────────────────

/**
 * Return all currently focusable elements inside `container`, in DOM order.
 *
 * Filters out:
 * - Invisible elements (display:none, visibility:hidden)
 * - aria-hidden elements and their descendants
 * - Elements outside a containing dialog (if `withinDialog` is specified)
 *
 * @param container   The root element to search within.
 * @param excludeSelf Whether to exclude `container` itself if it is focusable.
 */
export function getFocusableElements(container: HTMLElement, excludeSelf = true): HTMLElement[] {
  const candidates = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));

  // Also check container itself if it's focusable (e.g. tabindex="0" div)
  if (!excludeSelf && container.matches(FOCUSABLE_SELECTOR)) {
    candidates.unshift(container);
  }

  return candidates.filter((el) => isElementVisible(el) && !isAriaHidden(el));
}

/**
 * Return the first focusable element inside `container`, checking for:
 * 1. An element with `[data-autofocus]` attribute  ← explicit ERP convention
 * 2. An element with the HTML `autofocus` attribute
 * 3. The first element from `getFocusableElements()`
 */
export function getFirstFocusable(container: HTMLElement, selector?: string): HTMLElement | null {
  // Explicit selector takes priority
  if (selector) {
    const el = container.querySelector<HTMLElement>(selector);
    if (el && isElementVisible(el) && !isAriaHidden(el)) return el;
  }

  // ERP convention: [data-autofocus] marks the preferred initial field
  const dataAutofocus = container.querySelector<HTMLElement>('[data-autofocus]');
  if (dataAutofocus && isElementVisible(dataAutofocus) && !isAriaHidden(dataAutofocus)) {
    return dataAutofocus;
  }

  // HTML autofocus attribute
  const htmlAutofocus = container.querySelector<HTMLElement>('[autofocus]');
  if (htmlAutofocus && isElementVisible(htmlAutofocus) && !isAriaHidden(htmlAutofocus)) {
    return htmlAutofocus;
  }

  // First focusable in DOM order
  return getFocusableElements(container)[0] ?? null;
}

/**
 * Return the last focusable element inside `container`.
 */
export function getLastFocusable(container: HTMLElement): HTMLElement | null {
  const elements = getFocusableElements(container);
  return elements[elements.length - 1] ?? null;
}

// ─── Focus Predicates ─────────────────────────────────────────────────────────

/**
 * Returns true if the given element is currently focusable (visible, enabled,
 * not aria-hidden, and matches the focusable selector).
 */
export function isElementFocusable(el: HTMLElement): boolean {
  if (!el.matches(FOCUSABLE_SELECTOR)) return false;
  if (!isElementVisible(el)) return false;
  if (isAriaHidden(el)) return false;
  return true;
}

/**
 * Returns true if the given element (or a descendant) currently has focus.
 */
export function containsFocus(container: HTMLElement): boolean {
  const active = document.activeElement;
  return active !== null && container.contains(active);
}

// ─── Safe Focus ───────────────────────────────────────────────────────────────

/**
 * Focus an element, silently swallowing errors (e.g., element removed from DOM
 * between scheduling and execution).
 *
 * Returns true if focus was successfully applied.
 */
export function safelyFocus(
  el: HTMLElement | null | undefined,
  options: FocusOptions = { preventScroll: false },
): boolean {
  if (!el) return false;
  if (!document.contains(el)) return false;
  try {
    el.focus(options);
    return document.activeElement === el;
  } catch {
    return false;
  }
}

/**
 * Focus `el` using `requestAnimationFrame` to defer until after animations.
 * Useful when a dialog opens with a CSS transition and you want focus
 * to land after the animation completes.
 */
export function focusAfterFrame(el: HTMLElement | null | undefined, options?: FocusOptions): void {
  requestAnimationFrame(() => {
    safelyFocus(el, options);
  });
}
